import { cloneDeep } from 'lodash-es';
import type { PackageItemDisplayModel } from '@shared/models/packages/packageItems';
import type {
  IPackageItemImageNodeProperties,
  IPackageItemNodeProperties,
  PackageItemImageNodeDisplayModel,
  PackageItemTextNodeDisplayModel,
} from '@shared/models/packages/packageNodes';
import {
  isV3MultiImageDigitalDownload,
  isV3NoCropImage,
  DIGITAL_DOWNLOAD_CODES,
} from '@shared/models/products/productLookup';
import type { CartPackageImageNodeDisplayModel } from '../models/carts/cartPackageImageNodes';
import type { PackageDisplayModel } from '../models/packages/packages';

/**
 * Whether a text node is one of the canonical, customer-editable text fields.
 *
 * Only nodes named `Year` or `Text<n>` (e.g. `Text1`, `Text2`) qualify. Stray template nodes such
 * as `Node10004` are intentionally excluded.
 */
export const isCanonicalTextFieldName = (name: string | undefined | null): boolean => {
  if (!name) return false;
  return name === 'Year' || /^Text\d*$/i.test(name);
};

/**
 * A text node's "alias" is its data-field binding — `altDataSource` (set in the package editor,
 * e.g. "Name", "Team", "Jersey") or `dataSource` (from the DP2 template).
 */
export const hasTextNodeAlias = (
  node: { altDataSource?: string | null; dataSource?: string | null } | undefined | null,
): boolean => {
  if (!node) return false;
  return !!(node.altDataSource?.trim() || node.dataSource?.trim());
};

/**
 * `Year` is exempt from the alias requirement below. It is a self-describing field the user fills in
 * directly rather than a binding to imported subject data, so it legitimately carries no
 * altDataSource/dataSource and must stay visible on V3 jobs.
 *
 * Deliberately the same exact-case match `isCanonicalTextFieldName` uses, so this cannot widen which
 * nodes count as canonical.
 */
export const isYearFieldName = (name: string | undefined | null): boolean => name === 'Year';

/**
 * Whether a CartPackageTextNode should be surfaced to a consumer or studio user for editing on a
 * **V3 (RSPO)** job: it must be a canonical field name, and — unless it is `Year` — have an alias.
 * An unbound node such as `Text2` with no altDataSource/dataSource is meaningless to the user and must
 * be hidden — it would otherwise render as a stray "Text Line 2" box.
 *
 * This is the SINGLE source of truth for the V3 rule; every V3 branch calls it rather than composing
 * `isCanonicalTextFieldName` + `hasTextNodeAlias` inline, so the `Year` exemption can never drift
 * between surfaces.
 *
 * LEGACY MUST NOT USE THIS. Legacy node names and aliases are free-form (e.g. `Name`, `Grade`,
 * `Custom1` with no dataSource), so there is no alias to detect on that side and every editable node
 * is surfaced instead — see PR #2714, which removed the old `isLegacyConsumerTextNode` filter.
 * Always gate calls on the job being V3.
 */
export const isV3EditableTextNode = (
  node: { name?: string | null; altDataSource?: string | null; dataSource?: string | null } | undefined | null,
): boolean => {
  if (!node) return false;
  if (!isCanonicalTextFieldName(node.name)) return false;
  if (isYearFieldName(node.name)) return true;
  return hasTextNodeAlias(node);
};

/**
 * The subset of a text node package setup cares about. Deliberately structural: the same rule runs
 * against create models (product catalog), details models (item editor) and the V2 listing's flat
 * nodes, which agree on these fields and on nothing else.
 */
export type AssignableTextNode = {
  id?: number | string | null;
  name?: string | null;
  editable?: boolean;
  static?: boolean;
  dataSource?: string | null;
  altDataSource?: string | null;
  /** Server-set: the delivery email, which the studio may not reassign. */
  dataSourceLocked?: boolean;
  /** Studio chose "Leave Blank" in the editor: collect nothing, whatever the template bound. */
  leaveBlank?: boolean;
};

/** Only Text1–Text99 are offered for assignment; `Year` is filled in directly, not bound to data. */
const ASSIGNABLE_TEXT_NODE_NAME = /^text\d{1,2}$/i;

const isAssignableTextNode = (node: AssignableTextNode): boolean =>
  node.editable !== false && !node.static && ASSIGNABLE_TEXT_NODE_NAME.test(node.name ?? '');

/**
 * A package item's assignable text nodes, from either node shape — the details/create models nest
 * them under `pages[]`, the package listing flattens them onto the item.
 *
 * Both are read because the item editor ends up carrying both at once: it opens on the nested shape
 * and the step form writes the edited subset back to the flat one. Saved nodes are deduped by id so
 * that overlap is only reported once; unsaved nodes have no id and only ever appear in one shape.
 */
export const getAssignableTextNodes = (item: unknown): AssignableTextNode[] => {
  const host = (item ?? {}) as { textNodes?: unknown; pages?: unknown };

  const pageNodes = Array.isArray(host.pages)
    ? host.pages.flatMap((page) => {
        const nodes = (page as { textNodes?: unknown } | null)?.textNodes;
        return Array.isArray(nodes) ? (nodes as AssignableTextNode[]) : [];
      })
    : [];
  const rootNodes = Array.isArray(host.textNodes) ? (host.textNodes as AssignableTextNode[]) : [];

  const seen = new Set<number | string>();

  return [...pageNodes, ...rootNodes].filter((node) => {
    if (!isAssignableTextNode(node)) return false;
    if (node.id == null) return true;
    if (seen.has(node.id)) return false;

    seen.add(node.id);
    return true;
  });
};

/**
 * The field a node is bound to — what the lab looks up on the subject when the order is built.
 * `altDataSource` is the studio's assignment, `dataSource` the one the DP2 template shipped with.
 * A pending "Leave Blank" choice overrides both: the save clears the template binding as well.
 */
export const getTextNodeFieldAssignment = (node: AssignableTextNode): string =>
  node.leaveBlank ? '' : node.altDataSource?.trim() || node.dataSource?.trim() || '';

/** What to call a node in a message — its template binding if it has one, else the node name. */
export const getTextNodeLabel = (node: AssignableTextNode): string =>
  node.dataSource?.trim() || node.name || 'Text field';

/**
 * Text nodes the lab won't be able to resolve: the assignment names a field this organization type
 * doesn't define — a template whose TemplInfo points at a field that was never set up, or a sport
 * field left on a school job. The product would print subject data the studio never saw, so package
 * setup refuses to add or save the item.
 *
 * A node with no assignment at all is fine: that is the "Leave Blank" choice in the item editor. The
 * consumer never sees an unbound node (see `isV3EditableTextNode`) and it prints blank, which is what
 * the studio asked for.
 *
 * The delivery-email marker is exempt: it is deliberately not one of the defined fields, and the
 * studio cannot reassign it.
 */
export const getUnassignedTextNodes = (item: unknown, definedFieldNames: Iterable<string>): AssignableTextNode[] => {
  const known = new Set(
    [...definedFieldNames].map((name) => name?.trim().toLowerCase()).filter((name): name is string => !!name),
  );

  return getAssignableTextNodes(item).filter((node) => {
    if (node.dataSourceLocked) return false;

    const assignment = getTextNodeFieldAssignment(node);
    return !!assignment && !known.has(assignment.toLowerCase());
  });
};

/**
 * Check if a package item is a digital download (any type) that doesn't require cropping.
 */
const isDigitalDownloadItem = (item: PackageItemDisplayModel): boolean => {
  const code = item.productCode;
  return DIGITAL_DOWNLOAD_CODES.includes(code) || isV3MultiImageDigitalDownload(code) || isV3NoCropImage(code);
};

/**
 * The node classifiers below only consult `name` and `static`, so any node shape (package template
 * nodes, cart nodes with `imageLookupId: number | null`, ...) is accepted without casting.
 */
type NodeClassifierProps = Pick<IPackageItemNodeProperties, 'name' | 'static'>;

/**
 * Only Autofill individual image nodes when they are Image1 or Image2
 * All other image nodes should prompt the user to select an image
 */
export const isAutoFilledIndividualImageNode = (imageNode: NodeClassifierProps) => {
  const name = imageNode.name.toLowerCase();
  return name === 'image1' || name === 'image2';
};

export const isIndividualImageNode = (imageNode: NodeClassifierProps) => {
  return imageNode.name.toLowerCase().startsWith('image') && !imageNode.static;
};
export const isGroupImageNode = (imageNode: NodeClassifierProps) => {
  return imageNode.name.toLowerCase().startsWith('group') && !imageNode.static;
};
export const isCroppableNode = (imageNode: PackageItemImageNodeDisplayModel) => !!imageNode.positionCoords;

/**
 * Verify the imagenode is customizable by the consumer
 *
 * ***Rules:***
 * - Image name must be in the range of Image1 - Image98
 * - Image name must be in the range of Group1 - Group98
 */

export const isValidImageNode = (imageNode: CartPackageImageNodeDisplayModel | PackageItemImageNodeDisplayModel) => {
  // Name must contain Group or Image
  if (!imageNode.name.toLowerCase().includes('image') && !imageNode.name.toLowerCase().includes('group')) return false;

  // Test if the last two digits is a number and if it's lower than 99
  const doubleDigits = imageNode.name.slice(-2);
  if (Number.isInteger(parseInt(doubleDigits, 10)) && parseInt(doubleDigits, 10) < 99) return true;

  // Test if the last digit is a number
  const singleDigit = imageNode.name.slice(-1);
  if (Number.isInteger(parseInt(singleDigit, 10))) return true;

  return false;
};

export const getTextNodesForItem = (item: PackageItemDisplayModel): PackageItemTextNodeDisplayModel[] =>
  item.textNodes.filter((n) => !n.static);

export const getImageNodesForItem = (item: PackageItemDisplayModel): PackageItemImageNodeDisplayModel[] =>
  item.imageNodes.filter((n) => !n.static);

export const mergePackageItemTextNodes = (
  item: PackageItemDisplayModel,
  textNodeUpdates: PackageItemTextNodeDisplayModel[],
) => {
  const value = cloneDeep(item);
  value.textNodes = value.textNodes.map((tn) => ({
    ...tn,
    ...(textNodeUpdates.find((u) => u.id === tn.id) || {}),
  }));
  return value;
};

export const getAutoFillImageNodes = (pkg: PackageDisplayModel): PackageItemImageNodeDisplayModel[] => {
  return pkg.items
    .filter((item) => !isDigitalDownloadItem(item))
    .map((item) => getImageNodesForItem(item))
    .flat(2)
    .filter((n) => isAutoFilledIndividualImageNode(n) && isCroppableNode(n));
};

export interface PackageImageRequirements {
  requiresIndividual: boolean;
  requiresGroup: boolean;
}

/**
 * Determine which image types a package needs to be fulfilled, based on its
 * (non-static) image nodes across all items.
 * - `requiresIndividual` — has at least one individual image node (Image1, Image2, …)
 * - `requiresGroup` — has at least one group image node (Group1, Group2, …)
 */
export const getPackageImageRequirements = (pkg: PackageDisplayModel): PackageImageRequirements => {
  const imageNodes = pkg.items.map((item) => getImageNodesForItem(item)).flat(2);
  return {
    requiresIndividual: imageNodes.some(isIndividualImageNode),
    requiresGroup: imageNodes.some(isGroupImageNode),
  };
};
